import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Database, Sparkles, BarChart3, ArrowRight } from "lucide-react";
import { IntroVideo } from "@/components/dashboard/IntroVideo";

interface EmptyDashboardProps {
  companyName?: string;
}

const steps = [
  { icon: Upload, title: "Import Data", description: "Upload CSV, Excel, or connect a data source", number: 1 },
  { icon: Sparkles, title: "Clean & Analyze", description: "AI automatically cleans and processes your data", number: 2 },
  { icon: BarChart3, title: "View Insights", description: "Get AI-powered dashboards, predictions, and KPIs", number: 3 },
];

export const EmptyDashboard = ({ companyName }: EmptyDashboardProps) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <IntroVideo />
      <Card className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-2">
          Welcome{companyName ? `, ${companyName}` : ""}! 🎉
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Your AI analytics platform is ready. Start by importing your data to unlock powerful insights.
        </p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {steps.map((step) => (
          <Card key={step.number} className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                {step.number}
              </div>
              <step.icon className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-semibold mb-1">{step.title}</h3>
            <p className="text-sm text-muted-foreground">{step.description}</p>
          </Card>
        ))}
      </div>

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-lg">Get Started Now</h3>
            <p className="text-sm text-muted-foreground">Import your data or connect a source to begin</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate("/smart-data-entry")} className="gap-2">
              <Sparkles className="w-4 h-4" /> Smart Data Entry
            </Button>
            <Button variant="outline" onClick={() => navigate("/data")} className="gap-2">
              <Upload className="w-4 h-4" /> Import Data
            </Button>
            <Button variant="outline" onClick={() => navigate("/data")} className="gap-2">
              <Database className="w-4 h-4" /> Connect Source
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
